"use client";

import { SelectArrow, SelectItemCheck, SelectValue } from "@ariakit/react";
import type { GeoLocale } from "@trydig/next/i18n";
import { clsx, Slot } from "@trydig/ui";
import { atom, useAtom } from "jotai";
import type { ComponentProps } from "react";
import { Translate } from "@/components/Translate";
import { Button } from "@/components/ui/Button";
import {
	DrawerClose,
	DrawerContent,
	DrawerHeader,
	DrawerRoot,
	DrawerTitle,
	DrawerViewport,
} from "@/components/ui/Drawer";
import { useGeoPopup } from "@/components/ui/GeoPopup/hooks";
import {
	SelectItem,
	SelectLabel,
	SelectPopover,
	SelectRoot,
	SelectTrigger,
} from "@/components/ui/Select";
import { useTranslations } from "@/i18n/client";
import { countries, routing, useGeo } from "@/i18n/routing";
import { KlaviyoLocaleIdentify } from "@/lib/klaviyo/components";
import styles from "./GeoSelector.module.css";

export type GeoSelectorProps = {};

const geoOpen = atom(false);

const useGeoSelectorModal = () => {
	const [open, setOpen] = useAtom(geoOpen);

	return {
		open,
		setOpen,
	};
};

const _useLangMap = () => {
	const t = useTranslations();
	return {
		en: t("English"),
		no: t("Norwegian"),
	};
};

export const GeoSelectorModal = ({}: GeoSelectorProps) => {
	const { open, setOpen } = useGeoSelectorModal();
	const {
		action,
		pending,
		country: selectedCountry,
		language: selectedLanguage,
	} = useGeo();
	const { close } = useGeoPopup();

	const t = useTranslations();

	return (
		<DrawerRoot open={open} onOpenChange={setOpen}>
			<KlaviyoLocaleIdentify />
			<DrawerViewport color="yellow">
				<DrawerContent className={styles.content}>
					<DrawerClose />

					<DrawerHeader className={styles.header}>
						<DrawerTitle>{t("Select your location")}</DrawerTitle>
					</DrawerHeader>

					<form
						className={styles.form}
						action={(data) => {
							const language = data.get("language") as string;
							const country = data.get("country") as string;

							close();
							action(`${language}-${country}` as GeoLocale);
						}}
					>
						<SelectRoot defaultValue={selectedLanguage}>
							<SelectLabel className={styles.label}>
								{t("Select the desired language")}
							</SelectLabel>
							<SelectTrigger name="language">
								<SelectValue>
									{(value) => <Translate>{`languages.${value}`}</Translate>}
								</SelectValue>
								<SelectArrow />
							</SelectTrigger>
							<SelectPopover>
								{routing.languages.map((lang) => (
									<SelectItem key={lang} value={lang}>
										<Translate>{`languages.${lang}`}</Translate>
										<SelectItemCheck />
									</SelectItem>
								))}
							</SelectPopover>
						</SelectRoot>

						<SelectRoot defaultValue={selectedCountry}>
							<SelectLabel className={styles.label}>
								{t("Select shipping country")}
							</SelectLabel>
							<SelectTrigger name="country">
								<SelectValue>
									{(value) => {
										const selectedCountry = countries.find(
											(c) => c.country === value,
										)?.name;

										if (!selectedCountry) {
											return value;
										}

										return (
											<Translate>{`countries.${selectedCountry}`}</Translate>
										);
									}}
								</SelectValue>
								<SelectArrow />
							</SelectTrigger>
							<SelectPopover>
								{countries.map((c) => (
									<SelectItem key={c.country} value={c.country}>
										<Translate>{`countries.${c.name}`}</Translate>
										<SelectItemCheck />
									</SelectItem>
								))}
							</SelectPopover>
						</SelectRoot>

						<Button
							variant="default"
							loading={pending}
							type="submit"
							className={styles.confirmBtn}
						>
							<Translate>Confirm</Translate>
						</Button>
					</form>
				</DrawerContent>
			</DrawerViewport>
		</DrawerRoot>
	);
};

export const GeoSelectorTrigger = ({
	children,
	asChild,
	className,
	...rest
}: ComponentProps<"button"> & { asChild?: boolean }) => {
	const { setOpen } = useGeoSelectorModal();
	const Component = asChild ? Slot : "button";

	return (
		<Component
			className={clsx(styles.trigger, className)}
			{...rest}
			onClick={() => setOpen(true)}
		>
			{children}
		</Component>
	);
};
